import styles from "./Header.module.scss";
import { FC, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import cx from "classnames";
import { Button, MenuDropdown, Tooltip } from "src/components";
import { MenuOption } from "src/components/MenuDropdown/types";
import { ROUTES } from "src/constants";
import { CreateDashboardModal, CreateCustomDashboardModal } from "src/features";
import { useModal } from "src/hooks";
import { HeaderMessage, ProfileButton } from "./components";

import {
  Hamburger,
  Library,
  ThreeDots,
  Plus,
  Search,
  ChatAi,
} from "src/assets/icons";
import {
  selectWhiteLabel,
  selectCompanyTrackersCount,
} from "src/store/selectors";

// Inner imports

type Props = {
  showGlobalSearch: () => void;
};

export const Header: FC<Props> = memo(({ showGlobalSearch }) => {
  const { t } = useTranslation();

  const history = useHistory();

  const { setModal } = useModal();

  const trackersCount = useSelector(selectCompanyTrackersCount);

  const {
    name,
    logos: { small, medium },
  } = useSelector(selectWhiteLabel);

  const isDefaultDomain = useMemo<boolean>(() => name === "MyTelescope", [name]);

  const isCompileDashboardShown = useMemo<boolean>(
    () => trackersCount > 0,
    [trackersCount],
  );

  // Navigate to the original explore trend (dashboard search)
  const onExploreDashboardsClick = useCallback(
    (): void => history.push(ROUTES.exploreTrend),
    [history],
  );

  // Navigate to topic generate
  const onExploreClick = useCallback(
    (): void => history.push("/topic/generate"),
    [history],
  );

  const onCreateDashboardClick = useCallback((): void => {
    history.push(ROUTES.createTracker);
  }, [history]);

  const onCompileDashboardClick = useCallback((): void => {
    setModal("create-dashboard", <CreateDashboardModal />);
  }, [setModal]);

  const onCustomDashboardClick = useCallback((): void => {
    setModal("create-custom-dashboard", <CreateCustomDashboardModal />);
  }, [setModal]);

  // Define the dropdown options for the Create Dashboard button
  // IMPORTANT: This should NOT include Explore Trends
  const dashboardDropdownOptions = useMemo<MenuOption[]>(() => {
    // Start with an empty array
    const options: MenuOption[] = [];

    // Add Compile Dashboard if available
    if (isCompileDashboardShown) {
      options.push({
        icon: "Generate",
        onClick: onCompileDashboardClick,
        label: t("component.header.tooltip.compile_dashboard"),
      });
    }

    // Add Custom Dashboard option
    options.push({
      icon: "NoteAdd",
      onClick: onCustomDashboardClick,
      label: t("component.header.tooltip.custom_dashboard"),
    });

    return options;
  }, [
    t,
    onCompileDashboardClick,
    onCustomDashboardClick,
    isCompileDashboardShown,
  ]);

  // Define options for mobile menu
  // IMPORTANT: This should NOT include Explore Trends in the dropdown
  const mobileMenuOptions = useMemo<MenuOption[]>(() => {
    const options: MenuOption[] = [
      // Dashboard Search (kept in dropdown menu)
      {
        icon: "Search",
        onClick: onExploreDashboardsClick,
        label: t("component.header.tooltip.search_dashboard"),
      },
      // Global Search
      {
        icon: "Library",
        onClick: showGlobalSearch,
        label: t("component.header.tooltip.global_search"),
      },
    ];

    // Add Compile Dashboard if available
    if (isCompileDashboardShown) {
      options.unshift({
        icon: "Generate",
        onClick: onCompileDashboardClick,
        label: t("component.header.tooltip.compile_dashboard"),
      });
    }

    // Add Custom Dashboard option
    options.push({
      icon: "NoteAdd",
      onClick: onCustomDashboardClick,
      label: t("component.header.tooltip.custom_dashboard"),
    });

    return options;
  }, [
    t,
    onExploreDashboardsClick,
    showGlobalSearch,
    isCompileDashboardShown,
    onCompileDashboardClick,
    onCustomDashboardClick,
  ]);

  const shouldShowDashboardDropdown = useMemo<boolean>(
    () => dashboardDropdownOptions.length > 0,
    [dashboardDropdownOptions],
  );

  return (
    <header className={styles.wrapper}>
      <Link 
        to={ROUTES.DEFAULT_ROUTE} 
        className={cx(styles.logo, isDefaultDomain && styles.logoDefault)}
      >
        <picture>
          <source media="(max-width:768px)" srcSet={small} />
          <img src={medium} alt={name} />
        </picture>
      </Link>
      <div className={styles.buttonsSection}>
        <HeaderMessage />
        {shouldShowDashboardDropdown ? (
          <div className={styles.buttonDesktopWrapper}>
            <Button
              buttonSize="small"
              className={`${styles.addButton} ${styles.splitButtonMain}`}
              onClick={onCreateDashboardClick}
            >
              <Plus />
              <span>{t("component.header.label.create_dashboard")}</span>
            </Button>
            <MenuDropdown
              options={dashboardDropdownOptions}
              dropdownClassName={styles.dropdown}
              dropdownDirection={{ y: "down", x: "left" }}
            >
              <Button
                buttonSize="small"
                className={`${styles.button} ${styles.splitButtonMenu}`}
              >
                <ThreeDots />
              </Button>
            </MenuDropdown>
          </div>
        ) : (
          <Button
            buttonSize="small"
            className={styles.addButton}
            onClick={onCreateDashboardClick}
          >
            <Plus />
            <span>{t("component.header.label.create_dashboard")}</span>
          </Button>
        )}

        {/* Explore Trends Button */}
        <Button
          buttonSize="small"
          className={`${styles.exploreButton} ${styles.buttonDesktopWrapper}`}
          onClick={onExploreClick}
        >
          <ChatAi />
          <span>{t("component.header.label.explore_trend")}</span>
        </Button>

        {/* Dashboard Search Button */}
        <Tooltip
          content={t("component.header.tooltip.search_dashboard")}
          className={styles.buttonDesktopWrapper}
        >
          <Button
            buttonSize="small"
            className={styles.button}
            onClick={onExploreDashboardsClick}
          >
            <Search />
          </Button>
        </Tooltip>

        {/* Global Search Button */}
        <Tooltip
          content={t("component.header.tooltip.global_search")}
          className={styles.buttonDesktopWrapper}
        >
          <Button
            buttonSize="small"
            className={styles.button}
            onClick={showGlobalSearch}
          >
            <Library />
          </Button>
        </Tooltip>
        <div className={styles.buttonTabletWrapper}>
          {/* Primary action button for mobile */}
          <Tooltip content={t("component.header.tooltip.create_dashboard")}>
            <Button
              buttonSize="small"
              className={styles.button}
              onClick={onCreateDashboardClick}
            >
              <Plus />
            </Button>
          </Tooltip>
          {/* Explore button for mobile */}
          <Tooltip content="Explore Trends">
            <Button
              buttonSize="small"
              className={styles.button}
              onClick={onExploreClick}
            >
              <ChatAi />
            </Button>
          </Tooltip>
          {/* Menu button for all options */}
          <MenuDropdown
            options={mobileMenuOptions}
            dropdownClassName={styles.dropdown}
            dropdownDirection={{ y: "down", x: "left" }}
          >
            <Button buttonSize="small" className={styles.button}>
              <Hamburger />
            </Button>
          </MenuDropdown>
        </div>
        <ProfileButton />
      </div>
    </header>
  );
});
